import useUser from '@/features/auth/hooks/useUser'
import { UNIT_MAP, UNIT_MAP_V2, UnitConfig } from '@/hooks/constants'

export const useUnits = () => {
  const { data: user } = useUser()

  function formatNumber(number: number, isFixed: number) {
    return Number(
      new Intl.NumberFormat('en-US', {
        minimumFractionDigits: isFixed,
        maximumFractionDigits: isFixed,
      })
        .format(number)
        .replace(/,/g, ''),
    )
  }
  // const isMetric = !user?.measure?.id || user?.measure?.id === 1

  //console.log(user?.measure?.id) //id = 2 Imperial or id = 1 Metric
  const convertLabelUnit = (metricUnitLabel: string) => {
    try {
      if (user?.measure?.id === 1 || !user?.measure?.id) return metricUnitLabel
      return UNIT_MAP[metricUnitLabel].label
    } catch (e) {
      return metricUnitLabel
    }
  }

  const convertFromMetric = (value: any, metricUnit: string, isFixed = 2) => {
    try {
      if (isNaN(Number(value))) return -1
      if (user?.measure?.id === 1 || !user?.measure?.id) return value

      let result = UNIT_MAP[metricUnit].rate * Number(value)
      if (metricUnit === 'C') result += 32

      // Smart rounding: if result is very close to a whole number, round to it
      const rounded = Math.round(result)
      const difference = Math.abs(result - rounded)

      // If within 0.02 of a whole number, use the whole number
      if (difference <= 0.02) {
        return rounded.toString()
      }

      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: isFixed,
        maximumFractionDigits: isFixed,
      })
        .format(result)
        .replace(/,/g, '')
    } catch (e) {
      return value
    }
  }

  const convertToMetric = (value: any, metricUnit: string, isFixed = 2) => {
    try {
      if (isNaN(Number(value))) return -1
      if (user?.measure?.id === 1 || !user?.measure?.id) return value

      let result = Number(value) / UNIT_MAP[metricUnit].rate
      if (metricUnit === 'C') result -= 32

      // Smart rounding: if result is very close to a whole number, round to it
      const rounded = Math.round(result)
      const difference = Math.abs(result - rounded)

      // If within 0.02 of a whole number, use the whole number
      if (difference <= 0.02) {
        return rounded.toString()
      }

      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: isFixed,
        maximumFractionDigits: isFixed,
      })
        .format(result)
        .replace(/,/g, '')
    } catch (e) {
      return value
    }
  }

  const convertFromMetricV2 = (
    value: number,
    metricUnit: string,
    imperialUnit?: string,
    isFixed = 2,
  ) => {
    try {
      if (isNaN(Number(value))) return -1
      if (user?.measure?.id === 1 || !user?.measure?.id)
        return formatNumber(value, isFixed)

      const unitConfig = UNIT_MAP_V2[metricUnit]
      if (!unitConfig) return formatNumber(value, isFixed)

      const targetUnit = imperialUnit || unitConfig.defaultImperial
      const conversion = unitConfig.imperial[targetUnit]

      if (!conversion) return formatNumber(value, isFixed)

      let result = Number(value) * conversion.rate
      if (conversion.offset) {
        result += conversion.offset
      }

      // DELETE: Jon removed. This logic doesn't seem right. To remove the code after 1st Aug 2025 if it's still here
      // // Smart rounding: if result is very close to a whole number, round to it
      // const rounded = Math.round(result)
      // const difference = Math.abs(result - rounded)

      // // If within 0.0002 of a whole number, use the whole number
      // if (difference <= 0.0002) {
      //   return rounded
      // }

      return formatNumber(result, isFixed)
    } catch (e) {
      console.error('Error convert', e)
      return formatNumber(value, isFixed)
    }
  }

  const convertToMetricV2 = (
    value: number,
    metricUnit: string,
    fromImperialUnit?: string,
    isFixed = 2,
  ) => {
    try {
      if (isNaN(Number(value))) return -1
      if (user?.measure?.id === 1 || !user?.measure?.id) return value

      const unitConfig = UNIT_MAP_V2[metricUnit]
      if (!unitConfig) return value

      const sourceUnit = fromImperialUnit || unitConfig.defaultImperial
      const conversion = unitConfig.imperial[sourceUnit]

      if (!conversion) return value

      let result = Number(value)
      if (conversion.offset) {
        result -= conversion.offset
      }

      result = result / conversion.rate

      // Smart rounding: if result is very close to a whole number, round to it
      const rounded = Math.round(result)
      const difference = Math.abs(result - rounded)

      // If within 0.02 of a whole number, use the whole number
      if (difference <= 0.02) {
        return rounded
      }

      return Number(
        new Intl.NumberFormat('en-US', {
          minimumFractionDigits: isFixed,
          maximumFractionDigits: isFixed,
        })
          .format(result)
          .replace(/,/g, ''),
      )
    } catch (e) {
      console.error('Error convert', e)
      return value
    }
  }

  // TODO: v2 has major logic issue to skip converting if user is in metric (user?.measure?.id === 1). Need to double check and fix the rest
  const convertToMetricV3 = (
    value: number,
    metricUnit: string,
    fromImperialUnit?: string,
    isFixed = 2,
  ) => {
    try {
      if (isNaN(Number(value))) return -1

      const unitConfig = UNIT_MAP_V2[metricUnit]
      if (!unitConfig) return value

      const sourceUnit = fromImperialUnit || unitConfig.defaultImperial
      const conversion = unitConfig.imperial[sourceUnit]

      if (!conversion) return value

      let result = Number(value)
      if (conversion.offset) {
        result -= conversion.offset
      }

      result = result / conversion.rate

      // Smart rounding: if result is very close to a whole number, round to it
      const rounded = Math.round(result)
      const difference = Math.abs(result - rounded)

      // If within 0.02 of a whole number, use the whole number
      if (difference <= 0.02) {
        return rounded
      }

      return Number(
        new Intl.NumberFormat('en-US', {
          minimumFractionDigits: isFixed,
          maximumFractionDigits: isFixed,
        })
          .format(result)
          .replace(/,/g, ''),
      )
    } catch (e) {
      console.error('Error convert', e)
      return value
    }
  }

  const getAvailableImperialUnits = (metricUnit: string): string[] => {
    try {
      if (user?.measure?.id === 1 || !user?.measure?.id) {
        const unitConfig = UNIT_MAP_V2[metricUnit] as UnitConfig
        return unitConfig?.metricLabel ? [unitConfig.metricLabel] : []
      }

      const unitConfig = UNIT_MAP_V2[metricUnit]
      if (!unitConfig) return []

      return Object.keys(unitConfig.imperial)
    } catch (e) {
      return []
    }
  }

  const getImperialUnitLabel = (
    metricUnit: string,
    imperialUnit?: string,
  ): string => {
    try {
      if (user?.measure?.id === 1 || !user?.measure?.id) {
        const unitConfig = UNIT_MAP_V2[metricUnit] as UnitConfig
        return unitConfig?.metricLabel || metricUnit
      }

      const unitConfig = UNIT_MAP_V2[metricUnit] as UnitConfig
      if (!unitConfig) return ''

      const targetUnit = imperialUnit || unitConfig.defaultImperial
      return unitConfig.imperial[targetUnit]?.label || ''
    } catch (e) {
      return ''
    }
  }

  const convertFromMetricRawV2 = (
    value: number,
    metricUnit: string,
    imperialUnit?: string,
  ) => {
    try {
      if (isNaN(Number(value))) return -1
      if (user?.measure?.id === 1 || !user?.measure?.id) return value

      const unitConfig = UNIT_MAP_V2[metricUnit]
      if (!unitConfig) return value

      const targetUnit = imperialUnit || unitConfig.defaultImperial
      const conversion = unitConfig.imperial[targetUnit]

      if (!conversion) return value

      let result = Number(value) * conversion.rate
      if (conversion.offset) {
        result += conversion.offset
      }

      return result
    } catch (e) {
      console.error('Error convert', e)
      return value
    }
  }

  const convertToMetricRawV2 = (
    value: number,
    metricUnit: string,
    fromImperialUnit?: string,
  ) => {
    try {
      if (isNaN(Number(value))) return -1
      if (user?.measure?.id === 1 || !user?.measure?.id) return value

      const unitConfig = UNIT_MAP_V2[metricUnit]
      if (!unitConfig) return value

      const sourceUnit = fromImperialUnit || unitConfig.defaultImperial
      const conversion = unitConfig.imperial[sourceUnit]

      if (!conversion) return value

      let result = Number(value)
      if (conversion.offset) {
        result -= conversion.offset
      }

      result = result / conversion.rate

      return result
    } catch (e) {
      console.error('Error convert', e)
      return value
    }
  }

  return {
    isMetric: !user?.measure?.id || user?.measure?.id === 1,
    convertLabelUnit,
    convertFromMetric,
    convertToMetric,
    convertFromMetricV2,
    convertToMetricV2,
    convertToMetricV3,
    getAvailableImperialUnits,
    getImperialUnitLabel,
    convertFromMetricRawV2,
    convertToMetricRawV2,
  }
}

export const staticConvertToMetric = (
  value: number,
  metricUnit: string,
  fromImperialUnit?: string,
  isFixed = 2,
) => {
  try {
    if (isNaN(Number(value))) return -1

    const unitConfig = UNIT_MAP_V2[metricUnit]
    if (!unitConfig) return value

    const sourceUnit = fromImperialUnit || unitConfig.defaultImperial
    const conversion = unitConfig.imperial[sourceUnit]

    if (!conversion) return value

    let result = Number(value)
    if (conversion.offset) {
      result -= conversion.offset
    }

    result = result / conversion.rate

    // Smart rounding: if result is very close to a whole number, round to it
    const rounded = Math.round(result)
    const difference = Math.abs(result - rounded)

    // If within 0.02 of a whole number, use the whole number
    if (difference <= 0.02) {
      return rounded
    }

    return Number(
      new Intl.NumberFormat('en-US', {
        minimumFractionDigits: isFixed,
        maximumFractionDigits: isFixed,
      })
        .format(result)
        .replace(/,/g, ''),
    )
  } catch (e) {
    console.error('Error convert', e)
    return value
  }
}
